import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ApolloInstances } from '../graphql.module';
import {
  GraphqlAttachments,
  OfferAuditData,
  OfferProductPricesParams,
  SubmitOffersParams,
} from './offer';
import { GET_OFFERS_AUDIT_DATA } from './queries/get-offer-audit-data';
import {
  OfferPricingDomainSpecificAnalysisSummary,
  PriceIncreases,
  RfpOffer,
} from '../../../types/rfp/offer';
import GET_OFFER from './queries/get-offer';
import GET_OFFERS from './queries/get-offers';
import SET_OFFER_PRODUCTS_PRICES from './mutations/set-offer-products-prices';
import GET_PRICE_INCREASES from './queries/get-offer-price-Increases';
import GET_UPLOAD_FIELDS from './mutations/get-upload-fields';
import UPLOAD_PRODUCT_ANALYSIS from './mutations/upload-product-analysis';
import UPLOAD_PRODUCT_APPLY_CHANGES_REQUEST from './mutations/upload-product-apply-changes';
import UPLOAD_REQUEST_ANALYSIS_RESULTS from './queries/get-upload-request-analysis-results';
import SUBMIT_OFFERS from './mutations/submit-offers';
import GET_OFFER_PRICING_DOWNLOAD from './queries/get-offer-pricing-download';
import {
  AnalysisSummary,
  AnalysisSummaryWithoutCustomSummary,
  AssignableFieldsResponse,
  FieldMap,
  Sheet,
} from '../../../types/upload/api-upload-types';
import {
  AnalysisReportUrlResponse,
  ApplyChangesResponse,
} from '@aptitude/upload';
import { GET_OFFER_ATTACHMENTS } from './queries/get-offer-attachments';
import { GET_OFFER_ATTACHMENT_DOWNLOAD_URL } from './queries/get-offer-attachment-download-url';

interface OfferAnalysisSummary extends AnalysisSummaryWithoutCustomSummary {
  unorderedPricingCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class OfferGraphqlService {
  constructor(private apollo: Apollo) {}

  private validateParamIsCorrect(param: string, name: string) {
    if (!param?.length || param?.length === 0) {
      throw new Error(
        `The dev is trying to do a grapqhl request with an invalid ${name}: ${param}`,
      );
    }
    return true;
  }

  getOffer(id: number): Observable<RfpOffer> {
    this.validateParamIsCorrect(id.toString(), 'id');

    return this.apollo
      .use(ApolloInstances.OFFER)
      .query<{ offer: RfpOffer }>({
        fetchPolicy: 'no-cache',
        variables: { id },
        query: GET_OFFER,
      })
      .pipe(map((request) => request.data.offer));
  }

  uploadOfferPricingAssignableFields(
    uploadIdentifier: string,
    uploadRequestId: number,
    sheet: Sheet,
    resourceId: number,
  ): Observable<AssignableFieldsResponse> {
    return this.apollo
      .use(ApolloInstances.OFFER_UPLOAD_REQUEST)
      .mutate<{
        assignableFields: AssignableFieldsResponse;
      }>({
        variables: {
          uploadIdentifier,
          uploadRequestId,
          sheet,

          resourceId,
        },
        mutation: GET_UPLOAD_FIELDS,
      })
      .pipe(
        map((resp) => {
          if (!resp.data) {
            throw new Error(
              'uploadBulkProductEditAssignableFields must return data.',
            );
          }
          return resp.data.assignableFields;
        }),
      );
  }

  uploadOfferPricingAnalysisRequest(
    uploadRequestId: number,
    headerIndex: number,
    sheetName: string,
    fieldMap: FieldMap[],
  ): Observable<AnalysisSummary<OfferPricingDomainSpecificAnalysisSummary>> {
    return this.apollo
      .use(ApolloInstances.OFFER_UPLOAD_REQUEST)
      .mutate<{
        analysisRequest: OfferAnalysisSummary;
      }>({
        variables: {
          uploadRequestId,
          headerIndex,
          sheetName,
          fieldMap,
        },
        mutation: UPLOAD_PRODUCT_ANALYSIS,
      })
      .pipe(
        map((resp) => {
          if (!resp.data) {
            throw new Error('uploadProductAnalysisRequest must return data.');
          }
          return {
            ...resp.data.analysisRequest,
            domainSpecificSummary: {
              unorderedPricingCount:
                resp.data.analysisRequest.unorderedPricingCount,
            },
          };
        }),
      );
  }

  uploadRequestAnalysisResults = (
    uploadRequestId: number,
  ): Observable<AnalysisReportUrlResponse> => {
    return this.apollo
      .use(ApolloInstances.OFFER_UPLOAD_REQUEST)
      .query<{ uploadRequestAnalysisResults: AnalysisReportUrlResponse }>({
        query: UPLOAD_REQUEST_ANALYSIS_RESULTS,
        variables: {
          request: {
            uploadRequestId,
          },
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(map((resp) => resp.data.uploadRequestAnalysisResults));
  };

  uploadProductApplyChanges = (
    uploadRequestId: number,
  ): Observable<ApplyChangesResponse> => {
    return this.apollo
      .use(ApolloInstances.OFFER_UPLOAD_REQUEST)
      .query<{ uploadProductApplyChanges: ApplyChangesResponse }>({
        query: UPLOAD_PRODUCT_APPLY_CHANGES_REQUEST,
        variables: {
          uploadRequestId,
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(map((resp) => resp.data.uploadProductApplyChanges));
  };

  getPriceIncreases(offerId: number): Observable<PriceIncreases[]> {
    this.validateParamIsCorrect(offerId.toString(), 'id');

    return this.apollo
      .use(ApolloInstances.OFFER)
      .query<{ offer: RfpOffer }>({
        fetchPolicy: 'no-cache',
        variables: { id: offerId },
        query: GET_PRICE_INCREASES,
      })
      .pipe(map((request) => request.data.offer?.priceIncreases || []));
  }

  getOffers(rfpId: number): Observable<RfpOffer[]> {
    this.validateParamIsCorrect(rfpId.toString(), 'rfpId');

    return this.apollo
      .use(ApolloInstances.OFFER)
      .query<{ offersByRfpId: RfpOffer[] }>({
        fetchPolicy: 'no-cache',
        variables: {
          input: {
            rfpId,
          },
        },
        query: GET_OFFERS,
      })
      .pipe(map((request) => request.data.offersByRfpId));
  }

  getOfferAuditData(contextIds: number[]): Observable<OfferAuditData[]> {
    return this.apollo
      .use(ApolloInstances.OFFER)
      .query<{ offers: OfferAuditData[] }>({
        fetchPolicy: 'no-cache',
        variables: {
          criteria: {
            contextIds,
            contextName: 'ALL_OFFERS',
          },
        },
        query: GET_OFFERS_AUDIT_DATA,
      })
      .pipe(map(({ data }) => data.offers));
  }
  setOfferProductsPrices(
    pricesParams: OfferProductPricesParams,
  ): Observable<{ success: boolean }> {
    return this.apollo
      .use(ApolloInstances.OFFER)
      .mutate<{
        offerProductPrices: { success: boolean };
      }>({
        fetchPolicy: 'no-cache',
        variables: { input: pricesParams },
        mutation: SET_OFFER_PRODUCTS_PRICES,
      })
      .pipe(map((request) => request.data!.offerProductPrices));
  }
  submitOffers(input: SubmitOffersParams): Observable<{ success: boolean }> {
    return this.apollo
      .use(ApolloInstances.OFFER)
      .mutate<{
        submitOffers: { success: boolean };
      }>({
        fetchPolicy: 'no-cache',
        variables: { input },
        mutation: SUBMIT_OFFERS,
      })
      .pipe(map((request) => request.data!.submitOffers));
  }

  getOfferPricingDownload(offerId: number): Observable<string> {
    this.validateParamIsCorrect(offerId.toString(), 'offerId');

    return this.apollo
      .use(ApolloInstances.OFFER)
      .query<{ offerPricingDownload: { url: string } }>({
        fetchPolicy: 'no-cache',
        variables: { id: offerId },
        query: GET_OFFER_PRICING_DOWNLOAD,
      })
      .pipe(
        take(1),
        map((request) => request.data.offerPricingDownload.url),
      );
  }

  getOfferAttachments(offerId: number) {
    return this.apollo
      .use(ApolloInstances.OFFER)
      .query<{
        offer: {
          attachments: GraphqlAttachments;
        };
      }>({
        fetchPolicy: 'no-cache',
        variables: { id: offerId },
        query: GET_OFFER_ATTACHMENTS,
      })
      .pipe(
        take(1),
        map((response) => response.data.offer.attachments),
      );
  }

  downloadOfferAttachment(fileId: number, offerId: number) {
    return this.apollo
      .use(ApolloInstances.OFFER)
      .query<{ downloadOfferAttachment: { url: string } }>({
        query: GET_OFFER_ATTACHMENT_DOWNLOAD_URL,
        variables: {
          fileId,
          offerId,
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(map((request) => request.data.downloadOfferAttachment.url));
  }
}
